import { Flex, Text, VStack } from "@chakra-ui/react";
import { useFormatMoney } from "hooks/use-format-money";
import { PercentageBar } from "modules/vendor-portal/components/percentage-bar";
import { StatisticLink } from "modules/vendor-portal/screens/overview/components/price-indication/statistics-link";
import type {
  PriceIndicationGroup,
  PriceIndicationRange,
} from "modules/vendor-portal/screens/overview/components/price-indication/utils";

type DetailedPriceIndicationProps = {
  priceRange: PriceIndicationRange;
  priceGroups: PriceIndicationGroup[];
};

export function DetailedPriceIndication({
  priceRange,
  priceGroups,
}: DetailedPriceIndicationProps) {
  const formatMoney = useFormatMoney();
  return (
    <Flex direction={"column"} gap={5} w={"100%"}>
      <Flex direction={"row"} justify={"space-between"} align={"center"}>
        <VStack gap={0}>
          <Text fontSize={"md"} fontWeight={700} color={"gray.700"}>
            {`${formatMoney(priceRange.minValue)}`}
          </Text>
          <Text
            fontSize={"sm"}
            color={"gray.600"}
            w={"100%"}
            textAlign={"start"}
          >
            Lowest
          </Text>
        </VStack>
        <VStack gap={0}>
          <Text fontSize={"md"} fontWeight={700} color={"gray.700"}>
            {`${formatMoney(priceRange.averageValue)}`}
          </Text>
          <Text fontSize={"sm"} color={"gray.600"} textAlign={"center"}>
            Average
          </Text>
        </VStack>
        <VStack gap={0}>
          <Text fontSize={"md"} fontWeight={700} color={"gray.700"}>
            {`${formatMoney(priceRange.maxValue)}`}
          </Text>
          <Text fontSize={"sm"} color={"gray.600"} w={"100%"} textAlign={"end"}>
            Highest
          </Text>
        </VStack>
      </Flex>

      <Flex direction={"column"} justify={"center"} gap={3}>
        <PercentageBar
          label={
            <StatisticLink
              numberFilter={{ lte: priceGroups[0].end }}
            >{`< ${formatMoney(priceGroups[0].end)}`}</StatisticLink>
          }
          percentage={priceGroups[0].percentage}
        />

        <PercentageBar
          label={
            <StatisticLink
              numberFilter={{
                between: [priceGroups[1].start, priceGroups[1].end],
                inclusive: "upper",
              }}
            >
              {`${formatMoney(priceGroups[1].start)} - ${formatMoney(
                priceGroups[1].end,
              )}`}
            </StatisticLink>
          }
          percentage={priceGroups[1].percentage}
        />

        <PercentageBar
          label={
            <StatisticLink
              numberFilter={{
                between: [priceGroups[2].start, priceGroups[2].end],
              }}
            >
              {`${formatMoney(priceGroups[2].start)} - ${formatMoney(
                priceGroups[2].end,
              )}`}
            </StatisticLink>
          }
          percentage={priceGroups[2].percentage}
        />

        <PercentageBar
          label={
            <StatisticLink
              numberFilter={{
                gte: priceGroups[3].start,
              }}
            >{`${formatMoney(priceGroups[3].start)} +`}</StatisticLink>
          }
          percentage={priceGroups[3].percentage}
        />
      </Flex>
    </Flex>
  );
}
